import { Box } from '@mui/material'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPinnedColumnFields,
  GridColumnDimensions,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { PdfColumnConfig } from '@/core/inventory/downloadShareableInventoryPdf'
import { opticStockPrefix, SHIELD_STOCK_PREFIX, Vehicle } from '@otw/models/inventory'
import { useLocalStorageState } from '@toolpad/core'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { allocateStockNumber, stockAllocationErrorMessage } from 'core/inventory/allocateStockNumber'
import { generateOpticStockNumber, generateShieldStockNumber } from 'core/inventory/generateStockNumber'
import { removeFromWebsiteInventory } from 'core/inventory/removeFromWebsiteInventory'
import { addToWebsiteInventory } from 'core/inventory/showHideOnWebsiteInventory'
import { calculateProfit, sanitizeVehicleData } from 'core/utils/inventoryUtils'
import { DocumentData } from 'firebase/firestore'
import { getVehicleModelOptions, getVehicleSizeOptions } from 'constants/inventory'
import { normalizeInventoryV3Patch } from './inventoryV3CellValidation'
import { useInventory } from 'hooks/inventory/useInventory'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { InventoryCardView } from '@/components/inventory/cardView'
import DateAvailabilityDialog from '@/components/inventory/dialogs/DateAvailabilityDialog'
import ManagePhotosDialog from '@/components/inventory/dialogs/ManagePhotosDialog'
import ManageDocumentsDialog from '@/components/inventory/dialogs/ManageDocumentsDialog'
import PhotoUploadDialog from '@/components/inventory/dialogs/PhotoUploadDialog'
import SelectMainPhotoDialog from '@/components/inventory/dialogs/SelectMainWebsitePhotoDialog'
import SoldDialog from '@/components/inventory/dialogs/SoldDialog'
import {
  soldDialogShippingFromVehicle,
  storedShippingFromDialog,
} from '@/components/inventory/dialogs/soldShipping'
import PendingSaleNotPurchasedDialog from '@/components/inventory/dialogs/PendingSaleNotPurchasedDialog'
import VehicleForm from '@/components/inventory/forms/VehicleForm'
import FieldResetConfirmationDialog from '@/components/inventory/dialogs/FieldResetConfirmationDialog'
import { SaveTemplateDialog } from '@/components/inventory/dialogs/SaveTemplateDialog'
import TimelineDialog from '../Management/TransporterManagement/components/dialogs/TimelineDialog'
import { LoadingInventory } from '../../../components/Loading/LoadingInventory'
import { usePermissions } from 'hooks/auth/usePermissions'
import { PermissionDenied } from 'components'
import { useVehicleFormSession } from 'hooks/inventory/useVehicleFormSession'
import { useLeads } from 'hooks/admin/useLeads'
import { getPendingSaleLeadRequirementError } from 'views/SuperAdmin/Management/LeadManagement/util/leadDropoffValidation'
import { needsPendingSaleNotPurchasedGate } from '@/components/inventory/utils/pendingSaleNotPurchasedGate'
import { useFormulasWithRules } from 'hooks/graybook/useFormulasWithRules'
import { buildInventoryV3Rows, InventoryV3Row, sortInventoryV3RowsByDateAddedDesc } from './buildInventoryV3Rows'
import { getInventoryV3Columns } from './inventoryV3Columns'
import { InventoryV3Grid } from './InventoryV3Grid'
import { InventoryV3Header } from './InventoryV3Header'
import { FiltersPanel, InventoryV3Sidebar } from './InventoryV3Sidebar'
import { applyInventoryV3FilterModel } from './applyInventoryV3FilterModel'
import {
  DEFAULT_V3_COLUMN_VISIBILITY,
  DEFAULT_V3_TEMPLATE_ID,
  DEFAULT_V3_COLUMN_DIMENSIONS,
  DEFAULT_V3_COLUMN_ORDER,
  applyColumnDimensions,
  applyColumnOrder,
  ensureCheckboxPinnedLeft,
  mergeColumnDimensions,
  mergeColumnOrder,
} from './inventoryV3DefaultTemplate'
import { InventoryV3Totals, InventoryV3ViewMode } from './InventoryV3Footer'
import { parseSearchTerms, rowIsOpticOwned, rowMatchesQuickFilter } from './inventoryV3Search'
import {
  inventoryDocumentTabFromIndex,
  inventoryDocumentTabIndex,
  inventoryHashToRoute,
  inventoryV3ClearedFiltersPatch,
  inventoryV3FilterModelKey,
  inventoryV3RouteSearch,
  isInventoryV3ReloadNavigation,
  parseInventoryV3Route,
  writeInventoryV3Route,
  type InventoryV3DocTab,
  type InventoryV3Open,
  type InventoryV3RouteState,
} from './inventoryV3Route'
import { useInventoryV3Templates } from './useInventoryV3Templates'
import { useInventoryV3Zoom } from './useInventoryV3Zoom'
import { useInventoryV3SafetyTimeline } from './useInventoryV3SafetyTimeline'
import { useReadyForSaleVehicleIds } from './useReadyForSaleVehicleIds'
import { buildFundedUpdate, buildPaidForUpdate } from '@/components/inventory/utils/vehicleFinancialToggleUpdates'

const INVENTORY_V3_REVERT = 'INVENTORY_V3_REVERT'
const DIALOG_STATUSES = new Set(['sold', 'pending sale', 'active deal', 'pending available'])
const SOLD_DIALOG_STATUSES = new Set(['sold', 'pending sale', 'active deal'])
const COMPUTED_FIELDS = new Set([
  'id',
  'vehicle',
  'grayBookPrice',
  'ltv',
  'dealQuality',
  'safety',
  'isInPackage',
  'isDelivered',
  'sold_to_name',
  'sold_by_name',
  'selling_to_name',
  'delivered_to_customer',
  'ready_for_transport',
  'pickup_address',
  'photos',
  'documents',
  'timeline',
  'edit',
])
/** Never persist from the grid — same as AG Grid v2 (`editable: false` / revert stock_number). */
const GRID_READONLY_FIELDS = new Set(['stock_number', 'date_added', 'link', 'truck_notes', 'refinance'])
const namedUser = (value: unknown): string => {
  if (!value || typeof value !== 'object') return ''
  const name = (value as { name?: unknown }).name
  return typeof name === 'string' ? name : ''
}

/** Map a sold / pending-sale / date-available dialog write onto the grid row so processRowUpdate can commit once. */
const applyStatusDialogPatch = (
  oldRow: InventoryV3Row,
  newRow: InventoryV3Row,
  data: Record<string, unknown>,
): InventoryV3Row => {
  const status = typeof data.status === 'string' ? (data.status as InventoryV3Row['status']) : newRow.status
  const soldToName =
    data.sold_to_user === null
      ? ''
      : data.sold_to_user !== undefined
        ? namedUser(data.sold_to_user)
        : oldRow.sold_to_name
  const soldByName = data.sold_by_user !== undefined ? namedUser(data.sold_by_user) : oldRow.sold_by_name
  const sellingToName =
    data.selling_to_user === null
      ? ''
      : data.selling_to_user !== undefined
        ? namedUser(data.selling_to_user)
        : oldRow.selling_to_name

  return {
    ...newRow,
    status,
    sold_date: typeof data.sold_date === 'string' ? data.sold_date : oldRow.sold_date,
    date_available: typeof data.date_available === 'string' ? data.date_available : oldRow.date_available,
    location:
      typeof data.sold_location === 'string'
        ? data.sold_location
        : typeof data.location === 'string'
          ? data.location
          : oldRow.location,
    show_on_website: data.show_on_website === undefined ? oldRow.show_on_website : Boolean(data.show_on_website),
    sold_to_name: soldToName,
    sold_by_name: soldByName,
    selling_to_name: sellingToName,
    funded: data.funded === undefined ? oldRow.funded : Boolean(data.funded),
    vehicle: { ...oldRow.vehicle, ...data } as Vehicle,
  }
}

const DEFAULT_PINNED_COLUMNS = ensureCheckboxPinnedLeft({ left: [], right: [] })
/** Icon / action columns that carry no printable value. */
const PDF_SKIP_FIELDS = new Set([
  'photos',
  'documents',
  'timeline',
  'edit',
  '__check__',
  'safety',
  '__detail_panel_toggle__',
])

const InventoryV3 = ({ inventoryCollection }: { inventoryCollection: 'master_inventory' | 'shield_inventory' }) => {
  return <InventoryV3Content key={inventoryCollection} inventoryCollection={inventoryCollection} />
}

const InventoryV3Content = ({
  inventoryCollection,
}: {
  inventoryCollection: 'master_inventory' | 'shield_inventory'
}): JSX.Element => {
  const { userInfo } = useAuth()
  const { showError, showSuccess, showInfo } = useSnackbar()
  const { checkPermission, isPermissionsLoading } = usePermissions()
  const apiRef = useGridApiRef()

  const canViewInventory = checkPermission('inventory', 'view')
  const canCreateInventory = checkPermission('inventory', 'create')
  const canModifyInventory = checkPermission('inventory', 'modify')
  const canViewPurchasePrice = checkPermission('inventory', 'view_purchase_price')
  const canViewProfit = checkPermission('inventory', 'view_profit')
  const canViewProfitRealized = checkPermission('inventory', 'view_profit_realized')
  const canMarkSold = checkPermission('inventory', 'mark_sold')
  const canMarkSoldElsewhere = checkPermission('inventory', 'mark_sold_elsewhere')
  const canMarkReadyForSale = checkPermission('inventory', 'mark_ready_for_sale')
  const canMarkPendingSaleNotPurchased = checkPermission(
    'inventory',
    'mark_pending_sale_not_purchased',
  )

  const [openPhotoUploadDialog, setOpenPhotoUploadDialog] = useState(false)
  const [openDateAvailableDialog, setOpenDateAvailableDialog] = useState(false)
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false)
  const [deleteTemplateDialogOpen, setDeleteTemplateDialogOpen] = useState(false)
  const [pendingVehicleUpdate, setPendingVehicleUpdate] = useState<{
    id: string
    data: Record<string, unknown>
    onComplete?: (success: boolean) => void
    onCancel?: () => void
  } | null>(null)
  const [soldPopupOpen, setSoldPopupOpen] = useState(false)
  const [pendingSaleNotPurchasedOpen, setPendingSaleNotPurchasedOpen] = useState(false)
  const [pendingActionType, setPendingActionType] = useState<
    'sold' | 'sold_elsewhere' | 'pending_sale' | 'active_deal'
  >('sold')
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  // Browser refresh drops the query string; shared links (first navigate) still apply.
  const stripQueryOnReloadRef = useRef(
    isInventoryV3ReloadNavigation() && (searchParams.toString().length > 0 || Boolean(location.hash)),
  )
  // Optimistic route state: update synchronously so rapid header filters are not
  // reset by a still-stale controlled filterModel while setSearchParams catches up.
  const [route, setRoute] = useState(() =>
    parseInventoryV3Route(stripQueryOnReloadRef.current ? new URLSearchParams() : searchParams),
  )
  const routeRef = useRef(route)
  const skipUrlSyncRef = useRef(false)
  /** Block DataGrid filter echoes from undoing Clear Filters before the URL settles. */
  const suppressFilterModelSyncRef = useRef(false)
  const { viewMode, searchTerms, opticOwnedOnly, filterModel } = route
  const currentPage = route.page
  const itemsPerPage = route.perPage
  const replaceRoute = useCallback((patch: Partial<InventoryV3RouteState>) => {
    const merged = { ...routeRef.current, ...patch }
    routeRef.current = merged
    setRoute(merged)
    skipUrlSyncRef.current = true
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        // Always write the latest merged ref — not parse(prev)+patch — so queued
        // navigations cannot drop a newer filter when React Router races.
        writeInventoryV3Route(next, routeRef.current)
        if (next.toString() === prev.toString()) {
          skipUrlSyncRef.current = false
          return prev
        }
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])
  useEffect(() => {
    if (!stripQueryOnReloadRef.current) return
    navigate({ pathname: location.pathname, search: '', hash: '' }, { replace: true })
  }, [location.pathname, navigate])
  useEffect(() => {
    if (stripQueryOnReloadRef.current) {
      if (!searchParams.toString() && !location.hash) {
        stripQueryOnReloadRef.current = false
      }
      return
    }
    const parsed = parseInventoryV3Route(searchParams)
    const urlSearch = inventoryV3RouteSearch(parsed)
    const optimisticSearch = inventoryV3RouteSearch(routeRef.current)

    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false
      if (urlSearch === optimisticSearch) {
        // Adopt the canonical URL parse after our own write (drops unsafe fields).
        routeRef.current = parsed
        setRoute(parsed)
        return
      }
      // A stale navigation landed while a newer patch is in the ref — push the ref.
      skipUrlSyncRef.current = true
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          writeInventoryV3Route(next, routeRef.current)
          if (next.toString() === prev.toString()) {
            skipUrlSyncRef.current = false
            return prev
          }
          return next
        },
        { replace: true },
      )
      return
    }

    if (urlSearch === optimisticSearch) return
    routeRef.current = parsed
    setRoute(parsed)
  }, [searchParams, location.hash, setSearchParams])
  useEffect(() => {
    if (stripQueryOnReloadRef.current) return
    const fromHash = inventoryHashToRoute(location.hash)
    if (!fromHash) return
    const merged = { ...routeRef.current, ...fromHash }
    routeRef.current = merged
    setRoute(merged)
    skipUrlSyncRef.current = true
    const next = new URLSearchParams(location.search)
    writeInventoryV3Route(next, merged)
    const search = next.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: '' }, { replace: true })
  }, [location.hash, location.pathname, location.search, navigate])
  const [searchDraft, setSearchDraft] = useState('')
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    DEFAULT_V3_COLUMN_VISIBILITY,
  )
  const [columnDimensions, setColumnDimensions] =
    useState<Record<string, GridColumnDimensions>>(DEFAULT_V3_COLUMN_DIMENSIONS)
  const [orderedFields, setOrderedFields] = useState<string[]>(() => [...DEFAULT_V3_COLUMN_ORDER])
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>(DEFAULT_PINNED_COLUMNS)
  const [pendingFieldReset, setPendingFieldReset] = useState<{
    parentField: string
    parentValue: string
    dependentField: string
    dependentValue: string
    vehicleId: string
    patch: Record<string, unknown>
  } | null>(null)

  const isMobile = useIsMobile()
  const [view, setView] = useLocalStorageState<'list' | 'card'>(`${inventoryCollection}-inventory-v3-view`, 'list')
  /** Phone never mounts the grid — card view only. */
  const effectiveView: 'list' | 'card' = isMobile ? 'card' : (route.layout ?? view) || 'list'
  const { fontSize, rowHeight, zoomIn, zoomOut, canZoomIn, canZoomOut } = useInventoryV3Zoom(
    `${inventoryCollection}-inventory-v3-zoom-font`,
  )

  const {
    vehicles,
    addDocumentAsync,
    updateDocument,
    updateDocumentAsync,
    isLoading,
    deleteDocument,
    pauseSnapshotListener,
    resumeSnapshotListener,
  } = useInventory(inventoryCollection, undefined, { dontShowUpdates: true })
  const statusDialogWaiterRef = useRef<{
    resolve: (row: InventoryV3Row) => void
    oldRow: InventoryV3Row
    newRow: InventoryV3Row
  } | null>(null)

  const cancelStatusDialog = useCallback(() => {
    resumeSnapshotListener()
    const waiter = statusDialogWaiterRef.current
    if (!waiter) return
    statusDialogWaiterRef.current = null
    waiter.resolve(waiter.oldRow)
  }, [resumeSnapshotListener])

  const commitStatusDialog = useCallback(
    (data: Record<string, unknown>) => {
      const waiter = statusDialogWaiterRef.current
      if (waiter) {
        const row = applyStatusDialogPatch(waiter.oldRow, waiter.newRow, data)
        statusDialogWaiterRef.current = null
        waiter.resolve(row)
      }
      resumeSnapshotListener()
    },
    [resumeSnapshotListener],
  )
  const { leads } = useLeads()
  const { formulas } = useFormulasWithRules({})
  const { safetyByVehicleId, timelineByVehicleId } = useInventoryV3SafetyTimeline()
  const readyForSaleVehicleIdSet = useReadyForSaleVehicleIds()
  const routeTruck =
    route.truckId && vehicles ? vehicles.find(item => item.id === route.truckId) ?? null : null
  const isFormOpen = route.open === 'add' || (route.open === 'edit' && routeTruck != null)
  const formSelectedVehicle = route.open === 'edit' ? routeTruck : null
  const closeTruckSurface = useCallback(() => {
    replaceRoute({ open: null, truckId: null, docTab: null })
  }, [replaceRoute])
  const openTruckSurface = useCallback(
    (vehicle: Vehicle, open: InventoryV3Open, docTab?: InventoryV3DocTab) => {
      replaceRoute({
        open,
        truckId: vehicle.id,
        docTab: open === 'documents' ? docTab ?? 'title' : null,
      })
    },
    [replaceRoute],
  )
  const openAddForm = useCallback(() => {
    replaceRoute({ open: 'add', truckId: null, docTab: null })
  }, [replaceRoute])
  const openEditForm = useCallback(
    (vehicle: Vehicle) => {
      openTruckSurface(vehicle, 'edit')
    },
    [openTruckSurface],
  )
  const closeForm = closeTruckSurface
  const { templates, isLoadingTemplates, activeTemplateId, favoriteTemplateId, setFavoriteTemplate, applyTemplate, saveTemplate, updateTemplate, deleteTemplate, isSavingTemplate } =
    useInventoryV3Templates({
      apiRef,
      columnVisibilityModel,
      columnDimensions,
      orderedFields,
      pinnedColumns,
    })

  const addSearchTerms = useCallback(() => {
    const incoming = parseSearchTerms(searchDraft)
    if (incoming.length === 0) return
    const existing = new Set(route.searchTerms.map(term => term.toLowerCase()))
    const next = [...route.searchTerms]
    incoming.forEach(term => {
      if (!existing.has(term.toLowerCase())) {
        existing.add(term.toLowerCase())
        next.push(term)
      }
    })
    replaceRoute({ searchTerms: next, page: 1 })
    setSearchDraft('')
  }, [searchDraft, route.searchTerms, replaceRoute])

  const handleVehicleDataUpdate = async (
    vehicleId: string,
    data: { [key: string]: unknown },
    options?: {
      skipSoldDialog?: boolean
      skipDateAvailabilityDialog?: boolean
      onComplete?: (success: boolean) => void
      onCancel?: () => void
      previousColor?: string
    },
  ): Promise<boolean> => {
    const vehicle = vehicles?.find(item => item.id === vehicleId)
    const hasSoldDate = !!vehicle?.sold_date
    const isChangingSoldDate = Object.prototype.hasOwnProperty.call(data, 'sold_date') && data.sold_date !== vehicle?.sold_date

    if (hasSoldDate && isChangingSoldDate && !userInfo?.isOwner) {
      showError('Only the owner can modify the sold date of a vehicle')
      options?.onComplete?.(false)
      return false
    }

    const sanitizedData = sanitizeVehicleData(data, vehicle)
    const previousColor = options?.previousColor ?? vehicle?.color ?? 'White'
    if (
      sanitizedData &&
      'color' in sanitizedData &&
      typeof sanitizedData.color === 'string' &&
      sanitizedData.color !== previousColor
    ) {
      const validColors = ['White', 'Blue', 'Yellow', 'Being painted'] as const
      const to = sanitizedData.color as (typeof validColors)[number]
      if (validColors.includes(to)) {
        const newEntry = {
          color: to,
          changed_at: new Date().toISOString(),
          ...(userInfo?.id && { changed_by_user_id: userInfo.id }),
        }
        sanitizedData.color_history = [...(vehicle?.color_history || []), newEntry]
      }
    }

    if (!sanitizedData) {
      showError('Invalid value — change was not saved')
      options?.onComplete?.(false)
      return false
    }

    const newStatus = typeof sanitizedData.status === 'string' ? sanitizedData.status.toLowerCase().trim() : ''
    const currentStatus = vehicle?.status?.toLowerCase().trim() || ''
    const isChangingToSold =
      'status' in sanitizedData &&
      typeof sanitizedData.status === 'string' &&
      newStatus === 'sold' &&
      currentStatus !== 'sold' &&
      currentStatus !== 'sold elsewhere'
    const isChangingToPendingSale =
      'status' in sanitizedData &&
      typeof sanitizedData.status === 'string' &&
      newStatus === 'pending sale' &&
      currentStatus !== 'pending sale'
    const isChangingToActiveDeal =
      'status' in sanitizedData &&
      typeof sanitizedData.status === 'string' &&
      newStatus === 'active deal' &&
      currentStatus !== 'active deal'
    const isChangingToPendingAvailable =
      'status' in sanitizedData && typeof sanitizedData.status === 'string' && newStatus === 'pending available'

    if (isChangingToPendingSale) {
      const pendingSaleError = getPendingSaleLeadRequirementError(leads, vehicleId)
      if (pendingSaleError) {
        showError(pendingSaleError)
        options?.onComplete?.(false)
        return false
      }
    }

    if (isChangingToSold && !options?.skipSoldDialog) {
      delete sanitizedData.status
      pauseSnapshotListener()
      setPendingVehicleUpdate({
        id: vehicleId,
        data: sanitizedData,
        onComplete: options?.onComplete,
        onCancel: options?.onCancel,
      })
      setSoldPopupOpen(true)
      setPendingActionType('sold')
      return true
    }

    if ((isChangingToPendingSale || isChangingToActiveDeal) && !options?.skipSoldDialog) {
      delete sanitizedData.status
      pauseSnapshotListener()
      setPendingVehicleUpdate({
        id: vehicleId,
        data: sanitizedData,
        onComplete: options?.onComplete,
        onCancel: options?.onCancel,
      })
      setPendingActionType(isChangingToPendingSale ? 'pending_sale' : 'active_deal')

      if (
        isChangingToPendingSale &&
        vehicle &&
        needsPendingSaleNotPurchasedGate(vehicle, canMarkPendingSaleNotPurchased)
      ) {
        setPendingSaleNotPurchasedOpen(true)
        return true
      }

      setSoldPopupOpen(true)
      return true
    }

    if (isChangingToPendingAvailable && !options?.skipDateAvailabilityDialog) {
      delete sanitizedData.status
      setPendingVehicleUpdate({
        id: vehicleId,
        data: sanitizedData,
        onComplete: options?.onComplete,
        onCancel: options?.onCancel,
      })
      setOpenDateAvailableDialog(true)
      return true
    }

    try {
      await updateDocumentAsync({
        id: vehicleId,
        data: sanitizedData as Partial<DocumentData>,
      })
      showSuccess('Vehicle updated successfully')
      options?.onComplete?.(true)
      return true
    } catch (error) {
      console.error('Error updating vehicle:', error)
      showError('Error updating vehicle')
      options?.onComplete?.(false)
      return false
    }
  }

  const handleDateAvailabilityConfirm = async (date: string | null) => {
    if (!pendingVehicleUpdate || !date) {
      if (pendingVehicleUpdate) {
        showInfo('Status change cancelled - no availability date provided')
      }
      setOpenDateAvailableDialog(false)
      setPendingVehicleUpdate(null)
      return
    }

    const finalData = {
      ...pendingVehicleUpdate.data,
      date_available: date,
      status: 'Pending Available',
    }

    await handleVehicleDataUpdate(pendingVehicleUpdate.id, finalData, {
      skipDateAvailabilityDialog: true,
      onComplete: success => pendingVehicleUpdate.onComplete?.(success),
    })

    setOpenDateAvailableDialog(false)
    setPendingVehicleUpdate(null)
  }

  const handleRegenrateStockNumber = () => {
    const existing = (vehicles as Vehicle[])?.map(vehicle => vehicle.stock_number) || []
    if (inventoryCollection === 'shield_inventory') {
      return generateShieldStockNumber(existing)
    }
    return generateOpticStockNumber(existing, userInfo?.first_name || '', userInfo?.last_name || '')
  }

  const handleSoldSubmit = async (
    soldToUser: { id: string; name: string },
    soldByUser: { id: string; name: string },
    location: string,
    warrantyInfo?: {
      warrantyTerms: string | null
      warrantyCost: number | null
      customerCost: number | null
      warrantyProfit: number | null
    } | null,
    financeInfo?: {
      paymentType: string
      downPayment: number | null
      bankName: string | null
      amountFinanced: number | null
      points: number | null
      profit: number | null
      totalSalePrice: number | null
      taxRate: number | null
      includeTaxInSale: boolean
      includeWarrantyInSale: boolean
      includeShippingInSale: boolean
      transleaseLease?: boolean
    } | null,
    shippingInfo?: {
      shipped: boolean | null
      ourShippingCost: number | null
      customerShippingCost: number | null
      profit: number | null
    } | null,
    funded?: boolean,
  ): Promise<boolean> => {
    if (!pendingVehicleUpdate) return false

    const soldToUserData =
      soldToUser?.id && soldToUser?.name ? { id: String(soldToUser.id), name: String(soldToUser.name) } : { id: '', name: '' }
    const soldByUserData =
      soldByUser?.id && soldByUser?.name
        ? { id: String(soldByUser.id), name: String(soldByUser.name) }
        : { id: 'house', name: 'House' }

    const finalData: Record<string, unknown> = {
      ...pendingVehicleUpdate.data,
      sold_by_user: soldByUserData,
      sold_location: location,
    }

    if (pendingActionType === 'sold' || pendingActionType === 'sold_elsewhere') {
      const vehicle = vehicles?.find(item => item.id === pendingVehicleUpdate.id)
      const existingSoldDate = vehicle?.sold_date
      if (!finalData.sold_date && existingSoldDate) {
        finalData.sold_date = existingSoldDate
      } else if (!finalData.sold_date) {
        const today = new Date()
        today.setHours(12, 0, 0, 0)
        finalData.sold_date = today.toISOString()
      }
      finalData.show_on_website = false
      finalData.status = pendingActionType === 'sold' ? 'Sold' : 'Sold elsewhere'
      finalData.sold_to_user = soldToUserData
      finalData.selling_to_user = null
    } else {
      finalData.status = pendingActionType === 'pending_sale' ? 'Pending Sale' : 'Active Deal'
      finalData.selling_to_user = soldToUserData
    }

    if (financeInfo) {
      finalData.finance = {
        payment_type: financeInfo.paymentType,
        down_payment: financeInfo.downPayment !== null ? Number(financeInfo.downPayment) : null,
        bank_name: financeInfo.bankName,
        amount_financed: financeInfo.amountFinanced !== null ? Number(financeInfo.amountFinanced) : null,
        points: financeInfo.points !== null ? Number(financeInfo.points) : null,
        profit: financeInfo.profit !== null ? Number(financeInfo.profit) : null,
        total_sale_price: financeInfo.totalSalePrice !== null ? Number(financeInfo.totalSalePrice) : null,
        tax_rate: financeInfo.taxRate !== null ? Number(financeInfo.taxRate) : null,
        include_tax_in_sale: financeInfo.includeTaxInSale,
        include_warranty_in_sale: financeInfo.includeWarrantyInSale,
        include_shipping_in_sale: financeInfo.includeShippingInSale,
      }
      finalData.translease_lease = Boolean(financeInfo.transleaseLease)
    }

    if (warrantyInfo) {
      finalData.warranty_purchaged = true
      finalData.warranty = {
        warranty_terms: warrantyInfo.warrantyTerms,
        warranty_cost: warrantyInfo.warrantyCost !== null ? Number(warrantyInfo.warrantyCost) : null,
        customer_cost: warrantyInfo.customerCost !== null ? Number(warrantyInfo.customerCost) : null,
        warranty_created_at: new Date().toISOString(),
        warranty_profit: warrantyInfo.warrantyProfit !== null ? Number(warrantyInfo.warrantyProfit) : null,
      }
    }

    const storedShipping = storedShippingFromDialog(shippingInfo)
    if (storedShipping) {
      finalData.shipping = storedShipping
    }

    if (funded !== undefined && (pendingActionType === 'sold' || pendingActionType === 'active_deal')) {
      const vehicle = vehicles?.find(item => item.id === pendingVehicleUpdate.id)
      finalData.funded = funded
      finalData.funded_date = funded ? (vehicle?.funded_date ?? new Date().toISOString()) : null
    }

    const success = await handleVehicleDataUpdate(pendingVehicleUpdate.id, finalData, {
      skipSoldDialog: true,
      onComplete: pendingVehicleUpdate.onComplete,
    })
    if (success) commitStatusDialog(finalData)
    return success
  }

  const { clearFormData } = useVehicleFormSession(inventoryCollection, isFormOpen, true)

  const handleAddVehicle = async (data: { [key: string]: string | number | boolean }) => {
    const newValues = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined))
    newValues.date_added = new Date().toISOString()
    if (newValues.optic_list_price && newValues.seller_asking_price) {
      newValues.profit = calculateProfit(String(newValues.seller_asking_price), String(newValues.optic_list_price))
    }
    try {
      const prefix =
        inventoryCollection === 'shield_inventory'
          ? SHIELD_STOCK_PREFIX
          : opticStockPrefix(userInfo?.first_name || '', userInfo?.last_name || '')
      newValues.stock_number = await allocateStockNumber(prefix)
    } catch (error) {
      showError(stockAllocationErrorMessage(error))
      return false
    }
    try {
      await addDocumentAsync(newValues)
    } catch {
      showError('Error adding vehicle')
      return false
    }
    clearFormData()
    closeForm()
  }

  const handleEditVehicleForm = async (vehicleId: string, data: { [key: string]: string | number | boolean }) => {
    const success = await handleVehicleDataUpdate(vehicleId, data)
    if (success) closeForm()
    return success
  }

  const handleEditSelectedVehicleForm = async (data: { [key: string]: string | number | boolean }) => {
    if (!formSelectedVehicle) return false
    const success = await handleVehicleDataUpdate(formSelectedVehicle.id, data)
    if (success) closeForm()
    return success
  }

  const handleDeleteVehicle = (vehicleId: string) => {
    deleteDocument(vehicleId)
  }

  const handleManagePhotosClick = useCallback(
    (vehicle: Vehicle) => {
      openTruckSurface(vehicle, 'photos')
    },
    [openTruckSurface],
  )

  const handleManageDocumentsClick = useCallback(
    (vehicle: Vehicle, tab = 0) => {
      openTruckSurface(vehicle, 'documents', inventoryDocumentTabFromIndex(tab))
    },
    [openTruckSurface],
  )

  const handleTimelineClick = useCallback(
    (vehicle: Vehicle) => {
      openTruckSurface(vehicle, 'timeline')
    },
    [openTruckSurface],
  )

  const handleShowHideOnWebsiteClick = async (vehicle: Vehicle) => {
    if (vehicle.show_on_website !== true) {
      openTruckSurface(vehicle, 'website')
    }
    if (vehicle.show_on_website) {
      await removeFromWebsiteInventory(vehicle.id)
    } else {
      await addToWebsiteInventory(vehicle.id)
    }
    await updateDocument({
      id: vehicle.id,
      data: { show_on_website: !vehicle.show_on_website },
    })
  }

  const onBooleanFieldChange = (vehicleId: string, field: string, value: boolean) => {
    const vehicle = vehicles?.find(item => item.id === vehicleId)
    if (!vehicle) return

    if (field === 'ready_for_sale' && !canMarkReadyForSale) return
    if (field === 'profit_realized' && (!canViewProfitRealized || !canModifyInventory)) return
    if (field === 'show_on_website' && userInfo?.role !== 'admin') return
    if (
      field !== 'ready_for_sale' &&
      field !== 'show_on_website' &&
      field !== 'profit_realized' &&
      !canModifyInventory
    ) {
      return
    }

    if (field === 'show_on_website') {
      if (vehicle.status?.toLowerCase() === 'sold' && vehicle.show_on_website !== true) return
      void handleShowHideOnWebsiteClick(vehicle)
      return
    }

    if (field === 'paid_for') {
      void handleVehicleDataUpdate(
        vehicleId,
        buildPaidForUpdate(vehicle, value, userInfo?.email || userInfo?.id),
      )
      return
    }

    if (field === 'funded') {
      void handleVehicleDataUpdate(vehicleId, buildFundedUpdate(value))
      return
    }

    void handleVehicleDataUpdate(vehicleId, { [field]: value })
  }

  const rows = useMemo(
    () =>
      buildInventoryV3Rows({
        vehicles: vehicles ?? [],
        formulas,
        safetyByVehicleId,
        timelineByVehicleId,
        isOwner: userInfo?.isOwner === true || userInfo?.role === 'admin',
      }),
    [vehicles, formulas, safetyByVehicleId, timelineByVehicleId, userInfo?.isOwner, userInfo?.role],
  )

  const displayedRows = useMemo(() => {
    let filtered = rows
    if (viewMode === 'sold') {
      filtered = rows.filter(
        row => row.status?.toLowerCase() === 'sold' || row.status?.toLowerCase() === 'sold elsewhere',
      )
    } else if (viewMode === 'pending') {
      filtered = rows.filter(row => row.status?.toLowerCase() === 'pending available')
    } else if (viewMode === 'ready') {
      const excludedStatuses = new Set(['pending sale', 'sold', 'sold elsewhere'])
      filtered = rows.filter(row => {
        const status = row.status?.toLowerCase() ?? ''
        if (excludedStatuses.has(status)) return false
        return readyForSaleVehicleIdSet.has(row.id) || row.ready_for_sale === true
      })
    } else {
      filtered = rows.filter(
        row =>
          row.status?.toLowerCase() !== 'sold' &&
          row.status?.toLowerCase() !== 'sold elsewhere' &&
          row.status?.toLowerCase() !== 'pending available',
      )
    }
    if (opticOwnedOnly) {
      filtered = filtered.filter(row => rowIsOpticOwned(row))
    }
    return sortInventoryV3RowsByDateAddedDesc(filtered.filter(row => rowMatchesQuickFilter(row, searchTerms)))
  }, [rows, viewMode, readyForSaleVehicleIdSet, searchTerms, opticOwnedOnly])

  const selectedRowIds = useMemo(() => new Set(route.selectedIds), [route.selectedIds])

  const totals = useMemo<InventoryV3Totals>(() => {
    const source =
      selectedRowIds.size > 0 ? displayedRows.filter(row => selectedRowIds.has(row.id)) : displayedRows
    return source.reduce(
      (acc, row) => ({
        count: acc.count + 1,
        listPrice: acc.listPrice + (row.optic_list_price ?? 0),
        purchasePrice: acc.purchasePrice + (row.seller_asking_price ?? 0),
        profit: acc.profit + (row.profit ?? 0),
      }),
      { count: 0, listPrice: 0, purchasePrice: 0, profit: 0 },
    )
  }, [displayedRows, selectedRowIds])

  const selectedVehicles = useMemo(
    () => displayedRows.filter(row => selectedRowIds.has(row.id)).map(row => row.vehicle),
    [displayedRows, selectedRowIds],
  )

  const columns = useMemo(
    () =>
      applyColumnOrder(
        applyColumnDimensions(
          getInventoryV3Columns({
            isAdmin: userInfo?.role === 'admin',
            isBabyAdmin: userInfo?.role === 'babyAdmin',
            canModify: canModifyInventory,
            canViewPurchasePrice,
            canViewProfit,
            canViewProfitRealized,
            canMarkSold,
            canMarkSoldElsewhere,
            canMarkReadyForSale,
            onEditVehicleClick: openEditForm,
            onManagePhotosClick: handleManagePhotosClick,
            onManageDocumentsClick: handleManageDocumentsClick,
            onOpenTimelineClick: handleTimelineClick,
            onBooleanFieldChange,
          }),
          columnDimensions,
        ),
        orderedFields,
      ),
    [
      userInfo?.role,
      canModifyInventory,
      canViewPurchasePrice,
      canViewProfit,
      canViewProfitRealized,
      canMarkSold,
      canMarkSoldElsewhere,
      canMarkReadyForSale,
      openEditForm,
      handleManagePhotosClick,
      handleManageDocumentsClick,
      handleTimelineClick,
      onBooleanFieldChange,
      columnDimensions,
      orderedFields,
    ],
  )

  const cardVehicles = useMemo(
    () => applyInventoryV3FilterModel(displayedRows, filterModel, columns).map(row => row.vehicle),
    [displayedRows, filterModel, columns],
  )

  const onFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      if (suppressFilterModelSyncRef.current) return
      // Compare against the optimistic ref so a stale render closure cannot
      // re-write an older filterModel over a newer header selection.
      if (inventoryV3FilterModelKey(model) === inventoryV3FilterModelKey(routeRef.current.filterModel)) {
        return
      }
      replaceRoute({ filterModel: model, page: 1 })
    },
    [replaceRoute],
  )
  const cardFilterRef = useRef({
    columns,
    filterModel,
    onFilterModelChange,
    clearAllFilters: () => undefined as void,
    hasActiveFilters: false,
  })
  // clearAllFilters / hasActiveFilters are declared below; synced each render after they exist.
  cardFilterRef.current.columns = columns
  cardFilterRef.current.filterModel = filterModel
  cardFilterRef.current.onFilterModelChange = onFilterModelChange
  const CardFilterUI = useCallback(() => {
    const current = cardFilterRef.current
    return (
      <FiltersPanel
        columns={current.columns}
        filterModel={current.filterModel}
        onFilterModelChange={current.onFilterModelChange}
        clearAllFilters={current.clearAllFilters}
        hasActiveFilters={current.hasActiveFilters}
      />
    )
  }, [])

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(cardVehicles.length / itemsPerPage) || 1)
    if (vehicles && currentPage > totalPages) replaceRoute({ page: 1 })
  }, [vehicles, cardVehicles.length, itemsPerPage, currentPage, replaceRoute])

  const processRowUpdate = useCallback(
    async (newRow: InventoryV3Row, oldRow: InventoryV3Row): Promise<InventoryV3Row> => {
      const patch: Record<string, unknown> = {}
      const isAdminUser = userInfo?.role === 'admin'
      const isBabyAdminUser = userInfo?.role === 'babyAdmin'
      const canPatchField = (key: string): boolean => {
        if (COMPUTED_FIELDS.has(key) || GRID_READONLY_FIELDS.has(key)) return false
        if (key === 'seller_asking_price') return canModifyInventory && canViewPurchasePrice
        if (key === 'profit') return false
        if (key === 'profit_realized') return canModifyInventory && canViewProfitRealized
        if (key === 'ready_for_sale') return canMarkReadyForSale
        if (key === 'who') return canModifyInventory && (isAdminUser || isBabyAdminUser)
        if (key === 'vin' || key === 'phone_number' || key === 'show_on_website' || key === 'dealQuality') {
          return canModifyInventory && isAdminUser
        }
        if (key === 'sold_date') {
          const isVehicleSold = (newRow.status ?? oldRow.status)?.toLowerCase() === 'sold'
          return canModifyInventory && (!isVehicleSold || isAdminUser)
        }
        if (key === 'status') return canModifyInventory || canMarkSold || canMarkSoldElsewhere
        return canModifyInventory
      }
      ;(Object.keys(newRow) as (keyof InventoryV3Row)[]).forEach(key => {
        if (!canPatchField(key)) return
        if (newRow[key] !== oldRow[key]) {
          patch[key] = newRow[key]
        }
      })

      if (Object.keys(patch).length === 0) return oldRow

      const normalized = normalizeInventoryV3Patch(patch, {
        make: typeof patch.make === 'string' ? patch.make : oldRow.make,
        type: typeof patch.type === 'string' ? patch.type : oldRow.type,
      })
      if (!normalized.ok) throw new Error(normalized.message)
      Object.assign(patch, normalized.patch)
      Object.assign(newRow, normalized.patch)

      if (typeof patch.make === 'string' && patch.make !== oldRow.make) {
        const validModels = getVehicleModelOptions(patch.make)
        if (oldRow.model && !validModels.includes(oldRow.model) && oldRow.model !== 'Unknown') {
          setPendingFieldReset({
            parentField: 'Make',
            parentValue: patch.make,
            dependentField: 'Model',
            dependentValue: oldRow.model,
            vehicleId: newRow.id,
            patch: { make: patch.make, model: '' },
          })
          throw new Error(INVENTORY_V3_REVERT)
        }
      }

      if (typeof patch.type === 'string' && patch.type !== oldRow.type) {
        const validSizes = getVehicleSizeOptions(patch.type)
        if (oldRow.size && !validSizes.includes(oldRow.size) && oldRow.size !== 'Unknown') {
          setPendingFieldReset({
            parentField: 'Type',
            parentValue: patch.type,
            dependentField: 'Size',
            dependentValue: oldRow.size,
            vehicleId: newRow.id,
            patch: { type: patch.type, size: '' },
          })
          throw new Error(INVENTORY_V3_REVERT)
        }
      }

      if (
        (patch.optic_list_price !== undefined || patch.seller_asking_price !== undefined) &&
        newRow.optic_list_price != null &&
        newRow.seller_asking_price != null
      ) {
        patch.profit = calculateProfit(String(newRow.seller_asking_price), String(newRow.optic_list_price))
      }

      const nextStatus = typeof patch.status === 'string' ? patch.status.toLowerCase().trim() : ''
      if (SOLD_DIALOG_STATUSES.has(nextStatus)) {
        return new Promise<InventoryV3Row>(resolve => {
          statusDialogWaiterRef.current = { resolve, oldRow, newRow }
          void handleVehicleDataUpdate(newRow.id, patch, {
            previousColor: typeof patch.color === 'string' ? oldRow.color ?? undefined : undefined,
            onCancel: cancelStatusDialog,
          }).then(opened => {
            if (!opened) cancelStatusDialog()
          })
        })
      }

      const success = await handleVehicleDataUpdate(newRow.id, patch, {
        previousColor: typeof patch.color === 'string' ? oldRow.color ?? undefined : undefined,
      })
      if (!success) return oldRow
      if (DIALOG_STATUSES.has(nextStatus)) return oldRow
      return { ...newRow, vehicle: { ...oldRow.vehicle, ...patch } as Vehicle }
    },
    [
      vehicles,
      leads,
      userInfo,
      updateDocumentAsync,
      cancelStatusDialog,
      canModifyInventory,
      canViewPurchasePrice,
      canViewProfitRealized,
      canMarkSold,
      canMarkSoldElsewhere,
      canMarkReadyForSale,
    ],
  )

  const onProcessRowUpdateError = useCallback(
    (error: Error) => {
      if (error.message === INVENTORY_V3_REVERT) return
      showError(error.message || 'Could not update vehicle')
    },
    [showError],
  )

  const clearAllFilters = useCallback(() => {
    setSearchDraft('')
    suppressFilterModelSyncRef.current = true
    const cleared = inventoryV3ClearedFiltersPatch()
    replaceRoute(cleared)
    // Keep the grid’s internal model in lockstep with the optimistic route (header filters).
    apiRef.current?.setFilterModel?.(cleared.filterModel ?? { items: [] })
    // DataGrid may emit onFilterModelChange after setFilterModel; ignore until the flush settles.
    window.setTimeout(() => {
      suppressFilterModelSyncRef.current = false
    }, 0)
  }, [apiRef, replaceRoute])

  const hasActiveFilters =
    searchTerms.length > 0 || filterModel.items.length > 0 || opticOwnedOnly
  cardFilterRef.current.clearAllFilters = clearAllFilters
  cardFilterRef.current.hasActiveFilters = hasActiveFilters

  const handleViewChange = (_event: unknown, newView: 'list' | 'card' | null) => {
    if (isMobile || !newView) return
    setView(newView)
    replaceRoute({ layout: newView })
  }

  /** `restoreState` does not echo back into controlled models, so mirror them by hand. */
  const handlePinnedColumnsChange = useCallback((model: GridPinnedColumnFields) => {
    setPinnedColumns(ensureCheckboxPinnedLeft(model))
  }, [])

  const handleColumnWidthChange = useCallback((field: string, width: number) => {
    setColumnDimensions(current => ({
      ...current,
      [field]: { ...current[field], width },
    }))
  }, [])

  const handleColumnOrderChange = useCallback((fields: string[]) => {
    setOrderedFields(current => {
      const next = mergeColumnOrder(fields)
      if (current.length === next.length && current.every((field, index) => field === next[index])) {
        return current
      }
      return next
    })
  }, [])

  const handleApplyTemplate = useCallback(
    (templateId: string) => {
      const applied = applyTemplate(templateId || DEFAULT_V3_TEMPLATE_ID)
      if (!applied) return
      setColumnVisibilityModel(applied.columns?.columnVisibilityModel ?? {})
      setColumnDimensions(mergeColumnDimensions(applied.columns?.dimensions))
      setOrderedFields(mergeColumnOrder(applied.columns?.orderedFields))
      setPinnedColumns(ensureCheckboxPinnedLeft(applied.pinnedColumns ?? { left: [], right: [] }))
    },
    [applyTemplate],
  )

  const handleResetColumns = useCallback(() => {
    handleApplyTemplate(DEFAULT_V3_TEMPLATE_ID)
  }, [handleApplyTemplate])

  const didApplyFavorite = useRef(false)
  useEffect(() => {
    if (didApplyFavorite.current) return
    if (isLoading || isLoadingTemplates || !vehicles) return
    didApplyFavorite.current = true
    if (
      favoriteTemplateId !== DEFAULT_V3_TEMPLATE_ID &&
      templates.some(template => template.id === favoriteTemplateId)
    ) {
      handleApplyTemplate(favoriteTemplateId)
    }
  }, [favoriteTemplateId, handleApplyTemplate, isLoading, isLoadingTemplates, templates, vehicles])

  const getVisibleColumnsForPdf = useCallback(
    (): PdfColumnConfig[] =>
      columns
        .filter(column => columnVisibilityModel[column.field] !== false && !PDF_SKIP_FIELDS.has(column.field))
        .map(column => ({
          colId: column.field,
          headerName: column.headerName ?? column.field,
          field: column.field,
        })),
    [columns, columnVisibilityModel],
  )

  const isContentLoading = isLoading || isPermissionsLoading || !vehicles

  if (isContentLoading) {
    return <LoadingInventory />
  }

  if (!canViewInventory) {
    return (
      <PermissionDenied
        category='inventory'
        action='view'
        description='You need proper permissions to view and manage inventory.'
      />
    )
  }

  const pendingVehicle = pendingVehicleUpdate?.id
    ? vehicles?.find(item => item.id === pendingVehicleUpdate.id)
    : undefined

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...(effectiveView === 'list'
          ? {
              // App bar 64 + main padding + site footer (~68). Header stays put; only the grid scrolls.
              height: { xs: 'calc(100vh - 96px)', md: 'calc(100vh - 168px)' },
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      <InventoryV3Header
        inventoryCollection={inventoryCollection}
        isMobile={isMobile}
        canCreateInventory={canCreateInventory}
        canModifyInventory={canModifyInventory}
        view={effectiveView}
        onViewChange={handleViewChange}
        onOpenAddVehicleForm={openAddForm}
        searchDraft={searchDraft}
        onSearchDraftChange={setSearchDraft}
        onAddSearchTerms={addSearchTerms}
        searchTerms={searchTerms}
        onRemoveSearchTerm={term =>
          replaceRoute({ searchTerms: searchTerms.filter(item => item !== term), page: 1 })
        }
        onClearSearchTerms={() => replaceRoute({ searchTerms: [], page: 1 })}
        opticOwnedOnly={opticOwnedOnly}
        onOpticOwnedOnlyChange={owned => replaceRoute({ opticOwnedOnly: owned, page: 1 })}
        onClearFilters={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
      />

      {effectiveView === 'card' && (
        <InventoryCardView
          collectionName={inventoryCollection}
          vehicles={cardVehicles}
          FilterUI={CardFilterUI}
          SortUI={() => null}
          onEditVehicle={handleEditVehicleForm}
          onDeleteClick={handleDeleteVehicle}
          onManagePhotosClick={handleManagePhotosClick}
          onManageDocumentsClick={handleManageDocumentsClick}
          onOpenTimelineClick={handleTimelineClick}
          onShowHideOnWebsiteClick={handleShowHideOnWebsiteClick}
          activeTruckId={route.truckId}
          activeSurface={
            route.open === 'edit' || route.open === 'notes' || route.open === 'fields' ? route.open : null
          }
          onActiveSurfaceChange={(truckId, surface) => {
            if (!surface) {
              closeTruckSurface()
              return
            }
            const vehicle = cardVehicles.find(item => item.id === truckId)
            if (vehicle) openTruckSurface(vehicle, surface)
          }}
          canModify={canModifyInventory}
          canViewPurchasePrice={canViewPurchasePrice}
          canViewProfit={canViewProfit}
          canViewProfitRealized={canViewProfitRealized}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={page => replaceRoute({ page })}
          onItemsPerPageChange={perPage => replaceRoute({ perPage, page: 1 })}
        />
      )}

      {effectiveView === 'list' && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <InventoryV3Grid
            apiRef={apiRef}
            rows={displayedRows}
            columns={columns}
            canModify={canModifyInventory}
            isAdmin={userInfo?.role === 'admin'}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={onProcessRowUpdateError}
            rowSelectionModel={route.selectedIds}
            onRowSelectionModelChange={model => {
              if (!vehicles) return
              replaceRoute({ selectedIds: [...model].map(String) })
            }}
            expandedRowIds={route.expandedIds}
            onExpandedRowIdsChange={ids => {
              if (!vehicles) return
              replaceRoute({ expandedIds: ids.map(String) })
            }}
            viewMode={viewMode}
            onChangeViewMode={mode => replaceRoute({ viewMode: mode, page: 1 })}
            totals={totals}
            selectionActive={selectedRowIds.size > 0}
            canViewPurchasePrice={canViewPurchasePrice}
            canViewProfit={canViewProfit}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={handlePinnedColumnsChange}
            onColumnWidthChange={handleColumnWidthChange}
            onColumnOrderChange={handleColumnOrderChange}
            fontSize={fontSize}
            rowHeight={rowHeight}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
          />
          <InventoryV3Sidebar
            tab={route.sidebar}
            onTabChange={tab => replaceRoute({ sidebar: tab })}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={handlePinnedColumnsChange}
            onResetColumns={handleResetColumns}
            templates={templates}
            activeTemplateId={activeTemplateId}
            favoriteTemplateId={favoriteTemplateId}
            onApplyTemplate={handleApplyTemplate}
            onSetFavoriteTemplate={setFavoriteTemplate}
            onSaveTemplate={() => setSaveTemplateDialogOpen(true)}
            onUpdateTemplate={() => {
              if (activeTemplateId && activeTemplateId !== DEFAULT_V3_TEMPLATE_ID) {
                void updateTemplate(activeTemplateId)
              }
            }}
            onDeleteTemplate={() => {
              if (!activeTemplateId || activeTemplateId === DEFAULT_V3_TEMPLATE_ID) return
              setDeleteTemplateDialogOpen(true)
            }}
            isSavingTemplate={isSavingTemplate || isLoadingTemplates}
            selectedVehicles={selectedVehicles}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            clearAllFilters={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
            getVisibleColumnsForPdf={getVisibleColumnsForPdf}
          />
        </Box>
      )}

      <VehicleForm
        open={isFormOpen}
        inventoryCollection={inventoryCollection}
        vehicle={formSelectedVehicle}
        onClose={closeForm}
        onCancel={() => {
          clearFormData()
          closeForm()
        }}
        onSubmit={formSelectedVehicle ? handleEditSelectedVehicleForm : handleAddVehicle}
        generateStockNumber={handleRegenrateStockNumber}
        onReset={handleRegenrateStockNumber}
        canViewPurchasePrice={canViewPurchasePrice}
        canViewProfit={canViewProfit}
      />

      {routeTruck && (
        <>
          <PhotoUploadDialog
            open={openPhotoUploadDialog}
            onClose={() => setOpenPhotoUploadDialog(false)}
            vehicle={routeTruck}
          />
          <ManagePhotosDialog
            collectionName={inventoryCollection}
            open={route.open === 'photos'}
            onClose={closeTruckSurface}
            vehicle={routeTruck}
          />
          <ManageDocumentsDialog
            collectionName={inventoryCollection}
            open={route.open === 'documents'}
            onClose={closeTruckSurface}
            vehicle={routeTruck}
            initialTab={inventoryDocumentTabIndex(route.docTab)}
            onTabChange={index =>
              replaceRoute({ docTab: inventoryDocumentTabFromIndex(index), open: 'documents', truckId: routeTruck.id })
            }
          />
          <TimelineDialog
            open={route.open === 'timeline'}
            onClose={closeTruckSurface}
            vehicle={routeTruck}
          />
          <SelectMainPhotoDialog
            collectionName={inventoryCollection}
            open={route.open === 'website'}
            onClose={closeTruckSurface}
            vehicle={routeTruck}
          />
        </>
      )}

      <DateAvailabilityDialog
        open={openDateAvailableDialog}
        onClose={() => {
          setOpenDateAvailableDialog(false)
          setPendingVehicleUpdate(null)
        }}
        onConfirm={handleDateAvailabilityConfirm}
        currentDate={(pendingVehicleUpdate?.data?.date_available as string | null) || null}
        vehicleId={pendingVehicleUpdate?.id}
        vehicleInfo={pendingVehicleUpdate ? `Vehicle ID: ${pendingVehicleUpdate.id}` : undefined}
      />

      <SoldDialog
        open={soldPopupOpen}
        onClose={() => {
          pendingVehicleUpdate?.onCancel?.()
          cancelStatusDialog()
          setSoldPopupOpen(false)
          setPendingVehicleUpdate(null)
          setPendingActionType('sold')
        }}
        onSubmit={async (soldToUser, soldByUser, sold_location, warranty, finance, shipping, funded) => {
          const success = await handleSoldSubmit(
            soldToUser,
            soldByUser,
            sold_location,
            warranty,
            finance,
            shipping,
            funded,
          )
          if (success) {
            setSoldPopupOpen(false)
            setPendingVehicleUpdate(null)
            setPendingActionType('sold')
          }
          return success
        }}
        itemName={pendingVehicle?.stock_number || pendingVehicleUpdate?.id}
        price={Number(pendingVehicle?.optic_list_price) || 0}
        currentLocation={pendingVehicle?.location || 'Unknown'}
        actionType={pendingActionType}
        initialData={
          pendingVehicle
            ? {
                soldToUser:
                  pendingActionType === 'pending_sale' ||
                  pendingActionType === 'active_deal' ||
                  pendingVehicle.status?.toLowerCase() === 'pending sale' ||
                  pendingVehicle.status?.toLowerCase() === 'active deal'
                    ? pendingVehicle.selling_to_user || undefined
                    : pendingVehicle.sold_to_user || undefined,
                soldByUser: pendingVehicle.sold_by_user || undefined,
                soldLocation: pendingVehicle.sold_location || undefined,
                warranty: pendingVehicle.warranty
                  ? {
                      warrantyTerms: pendingVehicle.warranty.warranty_terms,
                      warrantyCost: pendingVehicle.warranty.warranty_cost,
                      customerCost: pendingVehicle.warranty.customer_cost,
                      warrantyProfit: pendingVehicle.warranty.warranty_profit,
                    }
                  : undefined,
                finance: pendingVehicle.finance
                  ? {
                      paymentType: pendingVehicle.finance.payment_type,
                      downPayment: pendingVehicle.finance.down_payment,
                      bankName: pendingVehicle.finance.bank_name,
                      amountFinanced: pendingVehicle.finance.amount_financed,
                      points: pendingVehicle.finance.points,
                      profit: pendingVehicle.finance.profit,
                      totalSalePrice: pendingVehicle.finance.total_sale_price,
                      taxRate: pendingVehicle.finance.tax_rate,
                      includeTaxInSale: pendingVehicle.finance.include_tax_in_sale,
                      includeWarrantyInSale: pendingVehicle.finance.include_warranty_in_sale,
                      includeShippingInSale: pendingVehicle.finance.include_shipping_in_sale,
                      transleaseLease: Boolean(pendingVehicle.translease_lease),
                    }
                  : {
                      paymentType: 'cash' as const,
                      downPayment: null,
                      bankName: null,
                      amountFinanced: null,
                      points: null,
                      profit: 0,
                      totalSalePrice: 0,
                      taxRate: null,
                      includeTaxInSale: false,
                      includeWarrantyInSale: false,
                      includeShippingInSale: false,
                      transleaseLease: Boolean(pendingVehicle.translease_lease),
                    },
                shipping: soldDialogShippingFromVehicle(pendingVehicle.shipping),
                funded: Boolean(pendingVehicle.funded) || Boolean(pendingVehicle.funded_date),
                fundedDate: pendingVehicle.funded_date ?? null,
              }
            : undefined
        }
      />

      <PendingSaleNotPurchasedDialog
        open={pendingSaleNotPurchasedOpen}
        onClose={() => {
          pendingVehicleUpdate?.onCancel?.()
          cancelStatusDialog()
          setPendingSaleNotPurchasedOpen(false)
          setPendingVehicleUpdate(null)
          setPendingActionType('sold')
        }}
        vehicles={
          pendingVehicle
            ? [
                {
                  id: pendingVehicle.id,
                  stock_number: pendingVehicle.stock_number,
                  pending_sale_not_purchased_notify:
                    pendingVehicle.pending_sale_not_purchased_notify,
                },
              ]
            : []
        }
        onUnlocked={() => {
          setPendingSaleNotPurchasedOpen(false)
          setSoldPopupOpen(true)
        }}
        onNotify={async vehiclesToNotify => {
          for (const vehicle of vehiclesToNotify) {
            if (vehicle.pending_sale_not_purchased_notify === true) continue
            await updateDocumentAsync({
              id: vehicle.id,
              data: { pending_sale_not_purchased_notify: true },
            })
          }
          pendingVehicleUpdate?.onCancel?.()
          cancelStatusDialog()
          setPendingSaleNotPurchasedOpen(false)
          setPendingVehicleUpdate(null)
          setPendingActionType('sold')
        }}
      />

      <SaveTemplateDialog
        open={saveTemplateDialogOpen}
        onClose={() => setSaveTemplateDialogOpen(false)}
        onSave={(name, description, setAsDefault) => {
          void saveTemplate({ name, description }).then(id => {
            if (setAsDefault && id) setFavoriteTemplate(id)
          })
        }}
        visibleColumns={columns
          .filter(column => columnVisibilityModel[column.field] !== false)
          .map(column => column.headerName || column.field)}
      />

      <ConfirmDialog
        open={deleteTemplateDialogOpen}
        title='Delete template'
        message={`Delete “${templates.find(template => template.id === activeTemplateId)?.name ?? 'this template'}”? This cannot be undone.`}
        confirmText='Delete'
        cancelText='Cancel'
        severity='error'
        onCancel={() => setDeleteTemplateDialogOpen(false)}
        onConfirm={() => {
          if (!activeTemplateId || activeTemplateId === DEFAULT_V3_TEMPLATE_ID) {
            setDeleteTemplateDialogOpen(false)
            return
          }
          setDeleteTemplateDialogOpen(false)
          void deleteTemplate(activeTemplateId)
        }}
      />

      {pendingFieldReset && (
        <FieldResetConfirmationDialog
          open
          onClose={() => setPendingFieldReset(null)}
          onConfirm={() => {
            void handleVehicleDataUpdate(pendingFieldReset.vehicleId, pendingFieldReset.patch)
            setPendingFieldReset(null)
          }}
          parentField={pendingFieldReset.parentField}
          parentValue={pendingFieldReset.parentValue}
          dependentField={pendingFieldReset.dependentField}
          dependentValue={pendingFieldReset.dependentValue}
        />
      )}
    </Box>
  )
}

export default InventoryV3
